import { makeAutoObservable, runInAction } from 'mobx'
import { showToast, type IDropdownItem } from 'shared/ui'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { InboxesApi } from 'entities/Inbox'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { TeamsPath } from 'pages/settings/pages/teams/route/teamsPath'

export class TeamsCreateStore {
  private _name = ''
  private _description = ''
  private _selectedMemberIds: number[] = []
  private _loading = false
  private _submitting = false

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = () => {
    this._entities.usersStore.initUsersOrganization()
  }

  get name() {
    return this._name
  }

  get description() {
    return this._description
  }

  get selectedMemberIds() {
    return this._selectedMemberIds
  }

  get loading() {
    return this._loading
  }

  get submitting() {
    return this._submitting
  }

  get canSubmit() {
    return this._name.trim().length > 0 && this._selectedMemberIds.length > 0 && !this._submitting
  }

  get membersItems(): IDropdownItem[] {
    return this._entities.usersStore.membersList.map((item) => ({
      id: item.id,
      label: item.name,
      value: item.name,
      data: item,
      avatarProps: {
        online: !item.isAway,
        size: 16,
        info: item.avatarInfo,
      },
    }))
  }

  get selectedMembersItems(): IDropdownItem[] {
    const list = this._entities.usersStore.membersList
    return this._selectedMemberIds
      .map((id) => list.find((m) => m.id === id))
      .filter((m): m is NonNullable<typeof m> => !!m)
      .map((item) => ({
        id: item.id,
        label: item.name,
        value: item.name,
        data: {
          isAway: undefined,
          avatarInfo: item.avatarInfo,
        },
      }))
  }

  setName = (value: string) => {
    this._name = value
  }

  setDescription = (value: string) => {
    this._description = value
  }

  setSelectedMemberIds = (ids: number[]) => {
    this._selectedMemberIds = ids
  }

  submit = async () => {
    runInAction(() => {
      this._submitting = true
    })
    try {
      await InboxesApi.createCoreGroup({
        members: this._selectedMemberIds,
        name: this._name.trim(),
        description: this._description,
      })

      uiStore.changeRoute({
        path: TeamsPath.root,
      })

      showToast({
        type: 'success',
        title: 'Team created',
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._submitting = false
      })
    }
  }

  dispose = () => {
    this._name = ''
    this._description = ''
    this._selectedMemberIds = []
  }
}
